import React from 'react';
import {
  get,
  map,
  take,
  reduce,
  isEmpty,
  filter,
  groupBy,
  orderBy
} from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { extractValue, maxScore, addBulletIfSet } from './shared';

const MAX_FAVORITES_COUNT = 10;
const LOADING_STRING = '--';

const getTrendFromIndex = index => (index < 0 ? -1 : 1);

const formatDate = date => {
  if (!date) {
    return '';
  }

  return moment(date).format('MMMM DD, YYYY');
};

export const isAiredShow = show => {
  const showAirDate = get(show, 'first_air_date', false);
  const firstEpisodeAirDate = get(show, 'firstEpisode.air_date', false);
  const today = moment();

  const showAired = showAirDate && today.diff(showAirDate) > 0;
  const epAired = firstEpisodeAirDate && today.diff(firstEpisodeAirDate) > 0;

  return showAired || epAired;
};

export const getAirDate = show =>
  get(show, 'first_air_date') || get(show, 'firstEpisode.air_date');

export const getRunStatus = show => {
  const status = get(show, 'status', '');
  const firstEpisodeAirDate = show.first_air_date;
  const lastEpisodeAirDate = show.series_last_air_date;

  if (status === 'Upcoming') {
    const airDate = getAirDate(show);
    return airDate ? (
      <>
        Upcoming {addBulletIfSet(1)} Premieres on {formatDate(airDate)}{' '}
      </>
    ) : (
      'Upcoming '
    );
  }

  if (status === 'Continuing') {
    return firstEpisodeAirDate ? (
      <>
        Still Running {addBulletIfSet(1)} {formatDate(firstEpisodeAirDate)} -
        Present{' '}
      </>
    ) : (
      'Still Running '
    );
  }

  if (status === 'Ended') {
    return firstEpisodeAirDate ? (
      <>
        Completed {addBulletIfSet(1)} {formatDate(firstEpisodeAirDate)} -{' '}
        {formatDate(lastEpisodeAirDate)}{' '}
      </>
    ) : (
      'Completed '
    );
  }

  return status;
};

export const formatCommentsFromQuery = (data = [], start, end) => {
  const dataToUse = start && end ? data.splice(start, end) : data;

  const formattedComments = map(dataToUse, comment => ({
    comment_date: comment.commentDate,
    content: comment.commentText,
    nb_likes: comment.likes,
    meme_url: comment.memeUrl,
    nb_replies: comment.replies
  }));

  return formattedComments;
};

export const getSeasonsInfo = show =>
  show.number_of_seasons > 1
    ? `${show.number_of_seasons} Seasons`
    : `${show.number_of_seasons} Season`;

export const getWatchInfo = show =>
  `${show.day_of_week_en} at ${show.air_time} - ${
    show.network
  } - ${getSeasonsInfo(show)} - ${getRunStatus(show)}`;

export const getEmotionVotes = ({ data = [] } = {}) => {
  if (isEmpty(data)) {
    return null;
  }

  const minSampleSize = 50;
  const sampleCount = get(data, [0, 'sampleCount'], 0);
  const totalVotesCount = get(data, [0, 'totalVotesCount'], 0);

  if (sampleCount < minSampleSize) {
    return null;
  }

  return `n= ${numeral(sampleCount).format('0,0')} | Total Votes ${numeral(
    totalVotesCount
  ).format('0,0')}`;
};

export const formatActorsFromQueryNew = ({ data = [] } = {}) => {
  const formattedCast = map(data, actor => ({
    id: actor.actorId,
    score: `${extractValue(actor, 'votingPercent')}%`,
    total: `${numeral(actor.votes).format('0,0')} votes`,
    name: actor.actorName,
    character: actor.characterName,
    image: actor.actorImageUrl
  }));

  return take(formattedCast, MAX_FAVORITES_COUNT);
};

export const formatActorsFromQuery = ({ data = [], fields = {} } = {}) => {
  const formattedCast = map(data, actor => ({
    id: actor[fields.actorId],
    score: `${extractValue(actor, fields.votingPercent)}%`,
    name: actor[fields.actorName],
    character: actor[fields.characterName],
    image: actor[fields.actorImageUrl]
  }));

  return take(formattedCast, MAX_FAVORITES_COUNT);
};

export const formatMobilityScores = ({ data = [] } = {}) => {
  if (isEmpty(data)) {
    return null;
  }

  const mobilityData = get(data, 0, {});
  const max = maxScore(mobilityData);

  return [
    {
      label: 'Phone',
      value: `${extractValue(mobilityData, 'phonePercent')}%`,
      subValue: `${numeral(get(mobilityData, 'phoneVotes', '-')).format(
        '0,0'
      )} viewers`,
      top: mobilityData.phonePercent === max
    },
    {
      label: 'Tablet',
      value: `${extractValue(mobilityData, 'tabletPercent')}%`,
      subValue: `${numeral(get(mobilityData, 'tabletVotes', '-')).format(
        '0,0'
      )} viewers`,
      top: mobilityData.tabletPercent === max
    },
    {
      label: 'Computer',
      value: `${extractValue(mobilityData, 'computerPercent')}%`,
      subValue: `${numeral(get(mobilityData, 'computerVotes', '-')).format(
        '0,0'
      )} viewers`,
      top: mobilityData.computerPercent === max
    },
    {
      label: 'TV',
      value: `${extractValue(mobilityData, 'tvPercent')}%`,
      subValue: `${numeral(get(mobilityData, 'tvVotes', '-')).format(
        '0,0'
      )} viewers`,
      top: mobilityData.tvPercent === max
    }
  ];
};

export const formatAudienceRatings = ({ data = [] } = {}) => {
  if (isEmpty(data)) {
    return null;
  }

  const dataObject = get(data, 0, {});
  return dataObject;
};

export const getSampleSize = ({ data = [] } = {}, blocked = false) =>
  blocked ? null : get(data, [0, 'viewers'], null);

export const createLoyaltyScore = ({ data = [] } = {}) => {
  const loyaltyScore = get(data, [0, 'completionScore'], false);
  const index = get(data, [0, 'completionIndex'], 0);

  return {
    trend: getTrendFromIndex(index),
    index: numeral(Math.abs(index * 100)).format('0'),
    value: loyaltyScore ? numeral(loyaltyScore).format('0%') : LOADING_STRING,
    label: 'Completion',
    tooltipMessage: '% of available episodes viewed by show followers'
  };
};

export const createEngagementScore = ({ data = [] } = {}) => {
  const engagementScore = get(data, [0, 'engagementScore'], false);
  const index = get(data, [0, 'engagementIndex'], 0);

  return {
    trend: getTrendFromIndex(index),
    index: numeral(Math.abs(index * 100)).format('0'),
    value: engagementScore
      ? numeral(engagementScore).format('00')
      : LOADING_STRING,
    label: 'Engagement',
    tooltipMessage: `Weighted score based on in-app engagement activity relative to tracked episodes.`
  };
};

export const createBingeScore = ({ data = [] } = {}) => {
  const bingeScore = get(data, [0, 'bingeScore'], false);
  const index = get(data, [0, 'bingeIndex'], 0);

  return {
    trend: getTrendFromIndex(index),
    index: numeral(Math.abs(index * 100)).format('0'),
    value: bingeScore ? numeral(bingeScore).format('0%') : LOADING_STRING,
    label: 'Binge',
    tooltipMessage:
      '% of viewers who reported viewing 4 or more episodes in 24 hours'
  };
};

export const formatShowViewersCount = ({ data = [], fields = {} } = {}) =>
  get(data, [0, fields.followers], get(data, [0, 'followers'], 0));

export const createViewShareScore = ({ data = [] } = {}, showId) => {
  const viewShare = get(data, [0, 'shareViews'], false);
  const index = get(data, [0, 'shareViewsIndex'], 0);

  const shareOfViewsLabel = (
    <span>
      Share
      <br />
      of Views
    </span>
  );

  return {
    trend: getTrendFromIndex(index),
    index: numeral(Math.abs(index * 100)).format('0'),
    value: viewShare ? numeral(viewShare).format('0.00%') : LOADING_STRING,
    label: shareOfViewsLabel,
    tooltipMessage: 'Share of overall views attributed to a particular Show',
    url: `/report/viewership/${showId}`
  };
};

export const getHottestMomentsTitle = (
  showName,
  seasonNumbers,
  episodeTitle
) => {
  let hottestMomentsTitle = (
    <span>
      (Beta) Hottest Moments from <em>{showName}</em>
    </span>
  );

  if (episodeTitle) {
    hottestMomentsTitle = `(Beta) Hottest Moments from ${episodeTitle}`;
  } else if (!isEmpty(seasonNumbers)) {
    hottestMomentsTitle = `(Beta) Hottest Moments from ${
      seasonNumbers.length === 1 ? 'Season' : 'Seasons'
    } ${
      seasonNumbers.length === 1
        ? seasonNumbers[0]
        : reduce(
            seasonNumbers,
            (acc, s, index) => {
              let builder = acc + s;
              if (index === seasonNumbers.length - 1) return builder;
              if (index < seasonNumbers.length - 1) {
                builder = `${builder}, and `;
              } else {
                builder = `${builder}, `;
              }

              return builder;
            },
            ''
          )
    }`;
  }

  return hottestMomentsTitle;
};

export const formatEmotionVotesByEpisode = (data = []) =>
  map(
    groupBy(
      map(data, row => ({
        episodeString: row.episodeString,
        episodeSort: row.episodeSort,
        [row.emotion]: numeral(row.emotionPercent * 100).format('0'),
        [`${row.emotion}Votes`]: row.emotionCount
      })),
      'episodeString'
    ),
    row => reduce(row, (acc, r) => ({ ...acc, ...r }), {})
  );

export const parseCharacterVoteRank = ({ data = [] } = {}) =>
  map(
    groupBy(
      map(
        orderBy(
          filter(data, character => get(character, 'voteRank', 0) <= 4),
          ['voteRank']
        ),
        row => ({
          rank: get(row, 'voteRank', 0),
          episode: get(row, 'seasonEpisodeLabel', ''),
          votes: get(row, 'votes', 0),
          percentage: get(row, 'votePercent', 0),
          characterImage: get(row, 'characterImageUrl', ''),
          characterName: get(row, 'characterName', ''),
          actorName: get(row, 'actorName', ''),
          actorId: get(row, 'actorId', 0)
        })
      ),
      'episode'
    ),
    (value, key) => ({ name: key, characters: value })
  );

export const getCurrentTabFromPath = (history, config, contentId) => {
  const paths = history.location.pathname.split('/');
  const lastPath = paths[paths.length - 1];

  if (lastPath === contentId || lastPath === 'anticipation') {
    return config.SHOW_ANTICIPATION_PAGE_ID;
  } else if (lastPath === 'audience') {
    return config.SHOW_VIEWERSHIP_PAGE_ID;
  } else if (lastPath === 'engagement') {
    return config.SHOW_ENGAGEMENT_PAGE_ID;
  } else if (lastPath === 'affinity') {
    return config.SHOW_AFFINITY_PAGE_ID;
  }

  return config.SHOW_ANTICIPATION_PAGE_ID;
};

export default {
  getWatchInfo,
  createLoyaltyScore,
  createEngagementScore,
  createBingeScore,
  formatMobilityScores,
  formatActorsFromQuery,
  formatCommentsFromQuery,
  formatShowViewersCount,
  getHottestMomentsTitle,
  parseCharacterVoteRank,
  isAiredShow,
  getSeasonsInfo,
  getAirDate,
  getCurrentTabFromPath
};
